import { UserClaims } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import React, { useState, useEffect } from 'react';
import { environment } from '../../environments/environment';
import LoginError from '../LoginError/LoginError';
import LoginLoading from '../LoginLoading/LoginLoading';
import StoreUnavailable from '../StoreUnavailable/StoreUnavailable';

import './Login.scss';

const Login = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const [retry, setRetry] = useState<number>(1);
  const [userInfo, setUserInfo] = useState<UserClaims | undefined>(undefined);
  const [signInError, setSignInError] = useState<
    Record<string, string | boolean>
  >({ message: '', displayToUser: false, retryable: false, clearCache: false });
  const [storeAvailable, setStoreAvailable] = useState<Record<string, boolean>>(
    { status: true, temporary: false }
  );

  useEffect(() => {
    async function oktaLogin() {
      console.log('Inside use effect', authState, retry, new Date());
      console.log('authState stringified: ', JSON.stringify(authState));

      if (!authState.isAuthenticated) return;
      try {
        const userInfo = await oktaAuth.getUser();
        console.log('userinfo: ', userInfo);
        setUserInfo(userInfo);
      } catch (err) {
        if ((err as {name: string;}).name === 'AuthApiError') {
          await oktaAuth.signInWithRedirect();
        }
      }

      const accessToken = oktaAuth.getAccessToken();

      console.log('accessToken: ', accessToken);
      const returnTo: string = sessionStorage.getItem('return_to') || '';
      fetch(
      `${environment.serviceUrl}/v1/shopify/multipass/login-url?return_to=${returnTo}`,
      {
        // Make call to service to get Shopify multipass login url
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          return response
            .json()
            .catch((err) => Promise.reject(err))
            .then((err) => Promise.reject((err && err.error) || err));
        }
        return response.json();
      })
      .then((response) => {
        console.log('data: ', response.data);
        window.location.href = response.data; // Redirect user to shopify store
      })
      .catch((err) => {
        /* eslint-disable no-console */
        console.error('Failed to get login url: ', err); // Log error in browser console
        console.log('error stringified: ', JSON.stringify(err));

        if (err && err.statusCode) {
          switch (err.statusCode) {
            case 400.101:
              setSignInError({
                message: 'There is no country configured in your Okta profile.',
                displayToUser: true,
              });
              break;
            case 400.102:
              // setSignInError('No store is available for your country.');
              setStoreAvailable({ status: false, temporary: false });
              break;
            case 400.103:
              setStoreAvailable({ status: false, temporary: true });
              break;
            case 400.104:
              setSignInError({
                message: 'Your country is not yet activated for store access.',
                displayToUser: true,
              });
              break;
            case 400.105:
              setSignInError({
                message:
                  'Your account is not yet enabled to access Online Game Store.',
                displayToUser: true,
              });
              break;
            case 400.106:
              setSignInError({
                message:
                  'Your Employee Type is not yet enabled to access Online Game Store.',
                displayToUser: true,
              });
              break;
            case 400.11:
              if (retry < 10) {
                setTimeout(() => {
                  console.log('Retrying...', retry, new Date());
                  setSignInError({
                    message: '',
                    displayToUser: false,
                    retryable: false,
                  });
                  setRetry((retry) => retry + 1);
                }, 1000);
              } else {
                setSignInError({
                  message: 'Failed in communicating with Shopify server',
                  displayToUser: false,
                  retryable: retry < 10, // Max 10 retries
                });
              }
              break;
            case 400.12:
              setSignInError({
                message: 'Failed at iHub',
                displayToUser: false,
              });
              break;
            default:
              setSignInError({
                message: (err && err.message) || String(err),
                displayToUser: false,
              });
          }
        } else {
          if (err.message === 'Unauthorized') {
            setSignInError({
              message: '',
              displayToUser: false,
              clearCache: true
            });
          } else {
            setSignInError({
              message: (err && err.message) || String(err),
              displayToUser: false,
            });
          }
        }
      });
    }

    oktaLogin();

    
  }, [authState?.isAuthenticated, retry]); // Update if authState changes

  const redirectToEAWorld = () => {
    window.location.href = 'https://eaworld.work.ea.com/home/';
  };

  return (
    <div className="login-container">
      {storeAvailable && storeAvailable.status ? (
        signInError && signInError.message ? (
          <LoginError error={signInError} onReturn={redirectToEAWorld} />
        ) : (
          <LoginLoading
            retry={retry}
            country={userInfo?.address?.country}
            name={userInfo?.name || ''}
          />
        )
      ) : (
        <StoreUnavailable
          temporary={storeAvailable.temporary}
          onReturn={redirectToEAWorld}
        />
      )}
    </div>
  );
};

export default Login;
